import {useModal} from '@hooks';
import {ModalPage, ModalPageBottom} from '@organisms';
import {useTranslation} from 'react-i18next';
import {Group, actions, useAppDispatch, useAppSelector} from '@lib/store';
import {useCallback, useMemo} from 'react';
import {toast} from 'react-hot-toast';
import {H4} from '@quarks';
import {GroupInviteHeader} from './GroupInviteHeader';
import {GroupInviteInvitedBySection} from './GroupInviteInvitedBySection';
import {GroupMemberItem} from '../GroupMemberItem';

export const GroupInviteModal = ({groupInvite}: {groupInvite: Group}) => {
  const {t} = useTranslation();
  const {closeModal} = useModal();
  const dispatch = useAppDispatch();

  const currentUserId = useAppSelector((state) => state.data.user.user.id);

  const invitedByUserId = useMemo(
    () => groupInvite?.members.find((member) => member.user.id === currentUserId)?.invitedByUser.id,
    [groupInvite, currentUserId],
  );

  const sortedMembers = useMemo(
    () =>
      groupInvite
        ? [...groupInvite.members].sort((a, b) => {
            const nameA = a.user.name.toUpperCase();
            const nameB = b.user.name.toUpperCase();

            if (invitedByUserId && invitedByUserId === a.user.id) return -1;
            if (invitedByUserId && invitedByUserId === b.user.id) return 1;

            return nameA.localeCompare(nameB);
          })
        : [],
    [groupInvite, invitedByUserId],
  );

  const pendingMutationType = useAppSelector((state) => {
    return state.data.groups.pendingMutations.find(
      (mutation) =>
        (mutation.type === 'acceptInvite' || mutation.type === 'declineInvite') &&
        mutation.data.groupId === groupInvite.id &&
        mutation.data.memberId === currentUserId,
    )?.type;
  });
  const isAcceptPending = pendingMutationType === 'acceptInvite';
  const isDeclinePending = pendingMutationType === 'declineInvite';

  const handleInviteAction = useCallback(
    async (actionType: 'accept' | 'decline') => {
      if (groupInvite) {
        const action = actionType === 'accept' ? actions.acceptGroupInvite : actions.declineGroupInvite;

        const resultAction = await dispatch(action({groupId: groupInvite.id, memberId: currentUserId}));

        const isFulfilled = action.fulfilled.match(resultAction);

        if (isFulfilled) {
          closeModal();
          toast.success(
            actionType === 'accept' ? t('groups:GroupInviteAcceptedToast') : t('groups:GroupInviteDeclinedToast'),
          );
        }
      }
    },
    [closeModal, currentUserId, dispatch, groupInvite, t],
  );

  const acceptInvite = useCallback(() => handleInviteAction('accept'), [handleInviteAction]);
  const declineInvite = useCallback(() => handleInviteAction('decline'), [handleInviteAction]);

  return (
    <ModalPage
      bottom={
        <ModalPageBottom
          buttonLoading={isAcceptPending || isDeclinePending}
          buttonLabel={t('translation:Accept')}
          cancelButtonLabel={t('translation:Decline')}
          onCancel={declineInvite}
          onClick={acceptInvite}
        />
      }
      onClose={closeModal}
      title={t('screen:GroupInvite')}>
      <div className="grid gap-6">
        <GroupInviteHeader group={groupInvite} />
        <GroupInviteInvitedBySection groupInvite={groupInvite} />

        <hr />

        <div>
          <H4 className="mb-2">{t('groups:GroupMembersLabel')}</H4>
          {sortedMembers.map((member) => (
            <GroupMemberItem
              key={member.user.id}
              member={member}
            />
          ))}
        </div>
      </div>
    </ModalPage>
  );
};
